import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
    AuthError,
    fetchAuthSession,
    signInWithRedirect,
    signOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import LoadingSpinner from '../LoadingSpinner';
import { getPrivacyRestrictedCountries } from 'src/helpers';
import { isTablet, isMobile, browserName } from 'react-device-detect';
import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from '@amzn/katal-metrics';
import {
    getCircuitState,
    recordAuthFailure,
    recordSuccess,
} from 'src/utils/authCircuitBreaker';

// Session-failure messages thrown from `fetchUser` below when
// `fetchAuthSession` resolves without usable tokens / sub. The breaker's
// allowlist in the `catch` matches on these exact strings — keep both ends
// pointed at the same constant so an edit to the throw-site can't silently
// stop the breaker from counting the failure.
const IDTOKEN_UNAVAILABLE = 'Cannot retrieve idToken';
const USER_ID_MISSING = 'no user id found!';

export interface User {
    id: string;
    name: string;
    country: string;
    hashedUserId: string;
}

export interface AuthContextType {
    user?: User;
    isPrivacyRestricted: () => boolean;
    tokenRefreshed: boolean;
}

export const AuthContext = React.createContext<AuthContextType>({
    isPrivacyRestricted: () => true,
    tokenRefreshed: false,
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState<User>();
    const [tokenRefreshed, setTokenRefreshed] = useState(false);

    const deviceMetricsPublisher =
        initialMetricsPublisher.newChildActionPublisherForMethod('Device');

    const fetchUser = async () => {
        try {
            const { tokens: session } = await fetchAuthSession();

            //  Check if there is a valid session
            if (!session?.idToken) throw new Error(IDTOKEN_UNAVAILABLE);

            if (!session?.idToken.payload['custom:userId']) {
                //  this property is being used as UID in Dynamo DB, just in case if token payload doesn't have it, user is being signed out, then redirected to login.
                signOut();
                throw new Error(USER_ID_MISSING);
            }

            const data = new TextEncoder().encode(
                String(session?.idToken.payload['custom:userId'])
            );
            const hashBuffer = await window.crypto.subtle.digest(
                'SHA-256',
                data
            );
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            const hashHex = hashArray
                .map((b) => b.toString(16).padStart(2, '0'))
                .join('');

            setUser({
                id: String(session?.idToken?.payload.sub),
                name: String(session?.idToken?.payload.given_name),
                country: String(session?.idToken.payload['custom:country']),
                hashedUserId: hashHex,
            });

            if (isTablet) {
                deviceMetricsPublisher.publish(
                    new KatalMetrics.Metric.String('gru.device', 'Tablet')
                );
            } else {
                // laptop/desktop or mobile
                deviceMetricsPublisher.publish(
                    new KatalMetrics.Metric.String(
                        'gru.device',
                        isMobile ? 'Mobile' : 'Laptop'
                    )
                );
                deviceMetricsPublisher.publish(
                    new KatalMetrics.Metric.String('gru.browser', browserName)
                );
            }

            // A successful bootstrap resets the breaker counter at this
            // boundary too, so an alternating fail/pass session cannot
            // accumulate toward the threshold between axios 2xx responses.
            recordSuccess();
        } catch (error) {
            console.log('fetchUser error: ', error);

            // Feed real Cognito/Federate session failures into the breaker.
            // AuthError covers the Amplify-wrapped path. The two plain-Error
            // messages below are thrown directly by this function (see the
            // IDTOKEN_UNAVAILABLE / USER_ID_MISSING constants) when
            // fetchAuthSession resolves without tokens / sub — same
            // Cognito-session root cause as the AuthError path, same loop
            // risk if signInWithRedirect fires again. Narrow allowlist on
            // the message strings so unrelated failures (SubtleCrypto,
            // generic I/O) still don't count.
            const isAmplifySessionFailure =
                error instanceof AuthError ||
                (error instanceof Error &&
                    (error.message === IDTOKEN_UNAVAILABLE ||
                        error.message === USER_ID_MISSING));
            if (isAmplifySessionFailure) {
                recordAuthFailure(401);
            }

            // If the circuit is open, defer to the ReAuthModal rather than
            // silently kicking off another signInWithRedirect — unbounded
            // redirect round-trips are the root cause of the loop.
            if (getCircuitState().open) {
                return;
            }

            //  redirect to Midway for SignIn
            signInWithRedirect({
                provider: {
                    custom: 'FederateSignIn',
                },
            });
        }
    };

    useEffect(() => {
        fetchUser();

        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            switch (payload.event) {
                case 'signInWithRedirect':
                    // If the circuit is open, the ReAuthModal is driving
                    // re-auth; skipping fetchUser here is the specific line
                    // that stops the Hub-event-driven loop in Firefox.
                    if (getCircuitState().open) {
                        break;
                    }
                    fetchUser();
                    break;
                case 'signedIn':
                case 'tokenRefresh':
                    setTokenRefreshed(true);
                    break;
                default:
                    console.log(payload);
            }
        });

        return unsubscribe;
    }, []);

    const isPrivacyRestricted = useCallback(() => {
        return (
            !user ||
            !user.country ||
            getPrivacyRestrictedCountries().includes(user.country)
        );
    }, [user]);

    const value: AuthContextType = {
        user,
        isPrivacyRestricted,
        tokenRefreshed,
    };

    if (!user) {
        return (
            <div data-testid="full-page-centered-spinner">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};
